import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Button, FormGroup, RotateLoader } from '@lib/components';
import { MobxComponent } from '../../../../../mobx/components';
import { UI } from '../../../../../core/ui';
import {
  IntervalToggle,
  PlanSummary,
  nativeSubscriptionActive,
  planName,
  planPeriodText,
  readCheckoutParams,
  stripCheckoutParams,
} from '../../../restaurant/views/settings/forms/integrations/native-loyalty-billing-ui';

interface Props {}
interface State {
  loaded: boolean;
  subscription: T.Schema.NativeLoyaltyBilling.Subscription | null;
  interval: T.Schema.NativeLoyaltyBilling.Interval;
  busy: boolean;
  error: string;
}

/** Organisation-wide Native Loyalty plan card on the dashboard Organization page. Buys/manages the
 * plan that unlocks Native Loyalty for every restaurant in the organisation. */
@inject('store')
@observer
export class OrganisationNativeLoyaltyPlan extends MobxComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { loaded: false, subscription: null, interval: 'month', busy: false, error: '' };
  }

  componentDidMount() {
    this.load();
  }

  load = async () => {
    const { store } = this.injected;
    try {
      const { checkout, sessionId } = readCheckoutParams();
      if (checkout === 'success' && sessionId) {
        const confirmed = await store.api.native_loyalty_org_billing_confirm({ session_id: sessionId });
        if (confirmed.outcome) {
          this.setState({ error: confirmed.message || 'Subscription confirmation failed' });
        } else {
          UI.notification.success('Subscribed to Quaddo organisation-wide');
        }
        stripCheckoutParams();
      } else if (checkout === 'cancel') {
        this.setState({ error: 'Checkout cancelled' });
        stripCheckoutParams();
      }
      const response = await store.api.native_loyalty_org_billing_status();
      if (response.outcome) {
        this.setState({ loaded: true, error: response.message || 'Failed to load subscription' });
        return;
      }
      this.setState({ loaded: true, subscription: response.data.subscription });
    } catch (e) {
      this.setState({ loaded: true, error: 'Failed to load subscription' });
    }
  };

  subscribe = async () => {
    const { store } = this.injected;
    this.setState({ busy: true, error: '' });
    try {
      const response = await store.api.native_loyalty_org_billing_checkout({
        interval: this.state.interval,
        return_origin: window.location.origin,
      });
      if (response.outcome) {
        this.setState({ busy: false, error: response.message || 'Subscription checkout failed' });
        return;
      }
      window.location.href = response.data.url;
    } catch (e) {
      this.setState({ busy: false, error: 'Subscription checkout failed' });
    }
  };

  manage = async () => {
    const { store } = this.injected;
    this.setState({ busy: true, error: '' });
    try {
      const response = await store.api.native_loyalty_org_billing_portal({ return_origin: window.location.origin });
      if (response.outcome) {
        this.setState({ busy: false, error: response.message || 'Failed to open subscription portal' });
        return;
      }
      window.location.href = response.data.url;
    } catch (e) {
      this.setState({ busy: false, error: 'Failed to open subscription portal' });
    }
  };

  render() {
    const { loaded, subscription, interval, busy, error } = this.state;
    const active = nativeSubscriptionActive(subscription);
    return (
      <>
        <h2 className="m-t-6 m-b-4">Quaddo</h2>
        <div className="p-4 bg-white">
          {!loaded && <RotateLoader size={2} />}
          {loaded && active && subscription && (
            <FormGroup
              title="Organisation plan"
              help="Unlocks Quaddo for every restaurant in this organisation. Change payment method or cancel from the subscription portal."
              no_border={true}
            >
              <PlanSummary>
                <span className="plan-name">{planName(subscription)}</span>
                <span className="plan-meta">{planPeriodText(subscription)}</span>
              </PlanSummary>
              <Button full={true} color="primary-inverse" type="button" onClick={this.manage} disabled={busy}>
                {busy && <RotateLoader size={2} />}
                {!busy && 'Manage subscription'}
              </Button>
            </FormGroup>
          )}
          {loaded && !active && (
            <FormGroup
              title="Organisation plan"
              help="One subscription that unlocks Quaddo for every restaurant in this organisation. Restaurants with their own Per Store plan stop renewing at the end of their current period."
              no_border={true}
            >
              <IntervalToggle value={interval} onChange={v => this.setState({ interval: v })} disabled={busy} />
              <Button full={true} color="primary" type="button" onClick={this.subscribe} disabled={busy}>
                {busy && <RotateLoader size={2} color="white" />}
                {!busy && 'Subscribe organisation-wide'}
              </Button>
            </FormGroup>
          )}
          {error && <FormGroup error={error} no_border={true} />}
        </div>
      </>
    );
  }
}
